import { useEffect, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ACTION_NOUNS, APP_CATEGORIES, type Action } from '@ward/core';
import { qs, useApi } from '../api';
import { Account, Ago, browserLabel, Button, catLabel, clickableRow, Empty, EntityLink, FilterChip, fmtBytes, fmtDate, ListToolbar, Notice, Outcome, outcomeTone, PageHead, PAGE_SIZE, Pager, Q, Section, Severity, SortTh, useFilters, type Filters } from '../ui/kit';
import { ThreatFacts } from './protect/health';

export interface EventRow {
  id: string;
  type: string;
  action: string;
  decision: string;
  outcome: string;
  severity: string | null;
  hostname: string;
  app_category: string;
  account_type: string;
  account_domain: string | null;
  account_identifier: string | null;
  account_provider: string;
  reported_email: string | null;
  file_name: string | null;
  file_ext: string | null;
  file_mime: string | null;
  file_size: number | null;
  justification: string | null;
  threat?: { category: string | null; source: string; reason: string } | null;
  detectors: Array<{ id: string; count: number }>;
  data_source: string | null;
  max_sensitivity: string | null;
  policy_version: number | null;
  inspect_ms: number | null;
  browser: string | null;
  occurred_at: string;
  received_at: string;
  user: { id: string; name: string; email: string } | null;
  device: { id: string; label: string; managed: boolean } | null;
  app: { id: string; name: string; approval: string } | null;
  rule: { id: string; name: string } | null;
  classificationDetails: Array<{ id: string; name: string; sensitivity: string | null }>;
}

export function actionLabel(a: string) {
  return ACTION_NOUNS[a as Action] ?? a;
}

/** One-line description, e.g. "paste of Payment card data". Never content. */
function what(e: EventRow) {
  const classes = e.classificationDetails.map((c) => c.name).join(', ');
  const file = e.file_name ? ` · ${e.file_name}` : '';
  return (
    <>
      <span className="capitalize">{actionLabel(e.action)}</span>
      {classes ? <> of <b className="fw-strong">{classes}</b></> : null}
      <span className="sub">{file}</span>
    </>
  );
}

/** `filters` makes the When column sortable (server-side `sort=±occurred_at`). */
export function EventTable({ rows, onOpen, compact, filters }: { rows: EventRow[]; onOpen: (id: string) => void; compact?: boolean; filters?: Filters }) {
  if (rows.length === 0) return <Empty title="No matching activity">Events appear when a policy acts, or when sensitive data is detected in an action.</Empty>;
  return (
    <div className="table-wrap">
      <table className="t">
        <thead>
          <tr>
            {filters ? <SortTh filters={filters} col="occurred_at" defaultDir="desc">When</SortTh> : <th>When</th>}
            <th>Outcome</th>
            <th>Action</th>
            {!compact && <th>User</th>}
            <th>Destination</th>
            <th>Account</th>
            {!compact && <th>Policy</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((e) => (
            <tr key={e.id} {...clickableRow(() => onOpen(e.id))}>
              <td className="sub nowrap"><Ago at={e.occurred_at} /></td>
              <td className="decision" data-tone={outcomeTone(e.outcome)}><Outcome o={e.outcome} /></td>
              <td>{what(e)}</td>
              {!compact && <td>{e.user ? <EntityLink to={`/discover/users/${e.user.id}`}>{e.user.name}</EntityLink> : <span className="sub">{e.reported_email ?? 'Unknown'}</span>}</td>}
              <td>{e.app?.name ?? e.hostname}<div className="sub">{catLabel(e.app_category)}</div></td>
              <td><Account t={e.account_type} domain={e.account_domain} /></td>
              {!compact && <td className="sub">{e.rule?.name ?? '—'}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * A page's event drawer, kept in the URL (`?event=<id>`) so events are linkable. Opening an event from inside the
 * drawer ("Around the same time") adds a history entry and shows "Back".
 */
export function useEventDrawer(): { open: (id: string) => void; drawer: ReactNode } {
  const [sp, setSp] = useSearchParams();
  const loc = useLocation();
  const nav = useNavigate();
  const id = sp.get('event');
  const show = (eid: string, nested: boolean) => {
    const n = new URLSearchParams(sp);
    n.set('event', eid);
    setSp(n, { state: nested ? { eventBack: true } : null });
  };
  const close = () => {
    const n = new URLSearchParams(sp);
    n.delete('event');
    setSp(n, { replace: true });
  };
  const back = (loc.state as { eventBack?: boolean } | null)?.eventBack ? () => nav(-1) : undefined;
  return { open: (eid) => show(eid, false), drawer: id ? <EventDrawer key={id} id={id} onClose={close} onOpen={(x) => show(x, true)} onBack={back} /> : null };
}

/**
 * Event detail. `onOpen` opens a nearby event (e.g. in the URL); without it the drawer keeps its own stack.
 * `onBack` returns to the event this one was opened from.
 */
export function EventDrawer({ id, onClose, onOpen, onBack }: { id: string; onClose: () => void; onOpen?: (id: string) => void; onBack?: () => void }) {
  const q = useApi<{ event: EventRow; nearby: EventRow[]; alerts: Array<{ id: string; title: string; status: string; severity: string }> }>(`/events/${id}`);
  const [inner, setInner] = useState<string | null>(null);
  useEffect(() => {
    if (inner) return;
    const k = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [onClose, inner]);
  if (inner) return <EventDrawer id={inner} onClose={onClose} onBack={() => setInner(null)} />;
  return (
    <>
      <div className="drawer-bg" onClick={onClose} />
      <aside className="drawer" role="dialog" aria-modal="true" aria-label="Event detail">
        <div className="page-head mb-8">
          <div className="row-8">
            {onBack && <Button size="small" variant="ghost" onClick={onBack}>← Back</Button>}
            <h1 className="text-lg">Event</h1>
          </div>
          <Button size="small" onClick={onClose}>Close</Button>
        </div>
        <Q q={q}>
          {({ event: e, nearby, alerts }) => (
            <>
              <div className="tags"><Outcome o={e.outcome} /><Severity s={e.severity} /></div>
              <dl className="facts">
                <dt>When</dt><dd>{fmtDate(e.occurred_at)} <span className="sub">(received {fmtDate(e.received_at)})</span></dd>
                <dt>Who</dt><dd>{e.user ? <Link to={`/discover/users/${e.user.id}`}>{e.user.name} · {e.user.email}</Link> : e.reported_email ?? 'Unidentified user'}</dd>
                <dt>Device</dt><dd>{e.device ? <Link to={`/discover/devices/${e.device.id}`}>{e.device.label}</Link> : '—'} {e.device && <span className="sub">{e.device.managed ? 'managed' : 'unmanaged'}{e.browser ? ` · ${browserLabel(e.browser)}` : ''}</span>}</dd>
                <dt>Action</dt><dd className="capitalize">{actionLabel(e.action)}</dd>
                <dt>Destination</dt><dd>{e.app ? <Link to={`/discover/apps/${e.app.id}`}>{e.app.name}</Link> : e.hostname} <span className="sub">· <code>{e.hostname}</code> · {catLabel(e.app_category)}</span></dd>
                <dt>Account</dt><dd><Account t={e.account_type} domain={e.account_domain} /> {e.account_identifier && <span className="sub">{e.account_identifier}</span>}</dd>
                <dt>Data</dt>
                <dd>
                  {e.classificationDetails.length ? e.classificationDetails.map((c) => <div key={c.id}>{c.name} <span className="sub">{c.sensitivity}</span></div>) : <span className="sub">No classified data detected</span>}
                  {e.detectors.length > 0 && <div className="sub">Detector matches: {e.detectors.map((d) => `${d.count}× ${d.id}`).join(', ')}</div>}
                  {e.data_source && <div className="sub">Source: {e.data_source}{e.inspect_ms !== null ? ` · inspected on device in ${e.inspect_ms} ms` : ''}</div>}
                </dd>
                {e.file_ext !== null && (<><dt>File</dt><dd>{e.file_name ?? <span className="sub">name not collected</span>} <span className="sub">· {e.file_mime} · {fmtBytes(e.file_size)}</span></dd></>)}
                <dt>Policy</dt><dd>{e.rule ? <Link to={`/manage/policies/${e.rule.id}`}>{e.rule.name}</Link> : 'No policy matched'}{e.policy_version !== null && <span className="sub"> · policy v{e.policy_version}</span>}</dd>
                {e.justification && (<><dt>Justification</dt><dd>“{e.justification}”</dd></>)}
                <ThreatFacts t={e.threat} />
                {alerts.length > 0 && (<><dt>Alerts</dt><dd>{alerts.map((a) => <div key={a.id}><Link to={`/investigate/alerts/${a.id}`}>{a.title}</Link></div>)}</dd></>)}
              </dl>
              <Notice className="mt-16">
                Content is never uploaded. This record holds only classifications and match counts computed on the device.
              </Notice>
              <Section title="Around the same time" aside="same user, ±30 minutes">
                <EventTable rows={nearby} onOpen={onOpen ?? setInner} compact />
              </Section>
            </>
          )}
        </Q>
      </aside>
    </>
  );
}

const OUTCOME_OPTS: Array<[string, string]> = [['', 'All outcomes'], ['blocked', 'Blocked'], ['warned_proceeded,warned_cancelled', 'Warned'], ['justified,justify_cancelled', 'Justified'], ['logged', 'Logged'], ['allowed', 'Allowed']];

/** Activity URL → /events query. `user`, `device` and `app` are ids (the "View activity" links of detail pages). */
function eventQuery(sp: URLSearchParams) {
  const g = (k: string) => sp.get(k) || undefined;
  return {
    q: g('q'), outcome: g('outcome'), action: g('action'), category: g('category'), accountType: g('accountType'), sensitiveOnly: g('sensitiveOnly'),
    userId: g('user'), deviceId: g('device'), appId: g('app'),
  };
}

export function Activity() {
  const filters = useFilters();
  const { sp, set, offset, setOffset } = filters;
  const ev = useEventDrawer();
  const f = eventQuery(sp);
  const q = useApi<{ total: number; items: EventRow[] }>(`/events${qs({ ...f, sort: sp.get('sort'), limit: PAGE_SIZE, offset })}`);
  const users = useApi<Array<{ id: string; name: string }>>('/users/options');
  const first = q.data?.items[0];
  const userOpts = users.data ?? [];
  const userMissing = !!f.userId && !userOpts.some((u) => u.id === f.userId);
  return (
    <>
      <PageHead
        title="Activity"
        sub="Security-relevant actions reported by enrolled browsers. Metadata only — never page, prompt, clipboard or file content."
        actions={<a className="btn" href={`/api/v1/events/export.csv${qs(f)}`}>Export CSV</a>}
      />
      <ListToolbar filters={filters} placeholder="Search user, application or hostname…">
        <select aria-label="User" value={f.userId ?? ''} onChange={(e) => set('user', e.target.value)}>
          <option value="">All users</option>
          {userMissing && <option value={f.userId}>{first?.user?.name ?? 'Selected user'}</option>}
          {userOpts.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
        <select aria-label="Outcome" value={f.outcome ?? ''} onChange={(e) => set('outcome', e.target.value)}>{OUTCOME_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        <select aria-label="Action" value={f.action ?? ''} onChange={(e) => set('action', e.target.value)}>
          <option value="">All actions</option>
          {['paste', 'upload', 'submit', 'download', 'navigate'].map((a) => <option key={a} value={a}>{actionLabel(a)}</option>)}
        </select>
        <select aria-label="Category" value={f.category ?? ''} onChange={(e) => set('category', e.target.value)}>
          <option value="">All categories</option>
          {APP_CATEGORIES.map((c) => <option key={c} value={c}>{catLabel(c)}</option>)}
        </select>
        <select aria-label="Account" value={f.accountType ?? ''} onChange={(e) => set('accountType', e.target.value)}>
          <option value="">Any account</option><option value="corporate">Corporate</option><option value="personal">Personal</option><option value="unknown">Unrecognised</option>
        </select>
        <label className="check"><input type="checkbox" checked={f.sensitiveOnly === 'true'} onChange={(e) => set('sensitiveOnly', e.target.checked ? 'true' : '')} /> Sensitive data only</label>
        {f.deviceId && <FilterChip label={<>Device: {first?.device?.label ?? 'selected device'}</>} onRemove={() => set('device', '')} />}
        {f.appId && <FilterChip label={<>Application: {first?.app?.name ?? 'selected application'}</>} onRemove={() => set('app', '')} />}
      </ListToolbar>
      <Q q={q}>
        {(d) => (
          <>
            <EventTable rows={d.items} onOpen={ev.open} filters={filters} />
            <Pager total={d.total} limit={PAGE_SIZE} offset={offset} onChange={setOffset} />
          </>
        )}
      </Q>
      {ev.drawer}
    </>
  );
}
